import { getRadioStationList, getRadioStationDetails, recordListeningActivity } from '../../components/Api/RadioStationApi';
import { getItem } from '../../Utils/localStorageUtils';
import { toast } from 'react-toastify';
import i18n from '../../Utils/Translate';

// Action Types
export const RADIO_STATION_ACTIONS = {
    // Station List
    GET_RADIO_STATIONS_START: 'GET_RADIO_STATIONS_START',
    GET_RADIO_STATIONS_SUCCESS: 'GET_RADIO_STATIONS_SUCCESS',
    GET_RADIO_STATIONS_FAILED: 'GET_RADIO_STATIONS_FAILED',
    
    // Station Details
    GET_RADIO_STATION_DETAILS_START: 'GET_RADIO_STATION_DETAILS_START',
    GET_RADIO_STATION_DETAILS_SUCCESS: 'GET_RADIO_STATION_DETAILS_SUCCESS',
    GET_RADIO_STATION_DETAILS_FAILED: 'GET_RADIO_STATION_DETAILS_FAILED',
    
    // Player State - Unified with Music
    SET_RADIO_CURRENT_STATION: 'SET_RADIO_CURRENT_STATION',
    SET_RADIO_PLAYING_STATUS: 'SET_RADIO_PLAYING_STATUS',
    SET_RADIO_VOLUME: 'SET_RADIO_VOLUME',
    SET_RADIO_MUTED: 'SET_RADIO_MUTED',
    TOGGLE_RADIO_PLAYER: 'TOGGLE_RADIO_PLAYER',
    
    // Modal Actions
    TOGGLE_RADIO_EPG_MODAL: 'TOGGLE_RADIO_EPG_MODAL',
    TOGGLE_RADIO_QUEUE_MODAL: 'TOGGLE_RADIO_QUEUE_MODAL',
    
    // EPG Data
    GET_RADIO_EPG_START: 'GET_RADIO_EPG_START',
    GET_RADIO_EPG_SUCCESS: 'GET_RADIO_EPG_SUCCESS',
    GET_RADIO_EPG_FAILED: 'GET_RADIO_EPG_FAILED',
    
    // Clear Actions
    CLEAR_RADIO_STATIONS: 'CLEAR_RADIO_STATIONS',
    CLEAR_RADIO_STATION_DETAILS: 'CLEAR_RADIO_STATION_DETAILS'
};

/**
 * Get Radio Stations List with filtering, search, and pagination
 */
export const getRadioStationsAction = (params = {}) => async (dispatch) => {
    try {
        dispatch({ type: RADIO_STATION_ACTIONS.GET_RADIO_STATIONS_START });
        
        const defaultParams = {
            Login_device_type: 'web',
            Login_user_type: parseInt(getItem('role'), 10) || 9,
            Login_user_id: parseInt(getItem('user_id'), 10) || null,
            limit: 9, // 3x3 grid
            offset: 0
        };
        
        const response = await getRadioStationList({
            ...defaultParams,
            ...params
        });
        
        if (response.data.status) {
            dispatch({
                type: RADIO_STATION_ACTIONS.GET_RADIO_STATIONS_SUCCESS,
                payload: {
                    stations: response.data.data || [],
                    pagination: response.data.pagination || {},
                    searchTerm: params.search || '',
                    categoryFilter: params.category_id || '',
                    languageFilter: params.language_id || ''
                }
            });
        } else {
            throw new Error(response.data.message || 'Failed to fetch radio stations');
        }
        
    } catch (error) {
        console.error('Radio stations fetch error:', error);
        dispatch({
            type: RADIO_STATION_ACTIONS.GET_RADIO_STATIONS_FAILED,
            payload: error.message || 'Failed to load radio stations'
        });
        toast.error(i18n.t('Failed to load radio stations'));
    }
};

/**
 * Get Radio Station Details
 */
export const getRadioStationDetailsAction = (slug) => async (dispatch) => {
    try {
        dispatch({ type: RADIO_STATION_ACTIONS.GET_RADIO_STATION_DETAILS_START });
        
        const response = await getRadioStationDetails({
            slug,
            Login_device_type: 'web',
            Login_user_type: parseInt(getItem('role'), 10) || 9,
            Login_user_id: parseInt(getItem('user_id'), 10) || null
        });
        
        if (response.data.status) {
            dispatch({
                type: RADIO_STATION_ACTIONS.GET_RADIO_STATION_DETAILS_SUCCESS,
                payload: response.data.data
            });
        } else {
            throw new Error(response.data.message || 'Station not found');
        }
        
    } catch (error) {
        console.error('Radio station details fetch error:', error);
        dispatch({
            type: RADIO_STATION_ACTIONS.GET_RADIO_STATION_DETAILS_FAILED,
            payload: error.message || 'Failed to load station details'
        });
        toast.error(i18n.t('Station not found'));
    }
};

/**
 * Play Radio Station - Unified with Music Player
 */
export const playRadioStationAction = (station) => async (dispatch, getState) => {
    try {
        // Clear any current music playback
        dispatch({ type: 'GET_PLAYING_SUCCESS', isPlaying: false });
        
        // Completely dynamic audio payload from API station data
        const audioPayload = {
            id: `radio_${station.id}`,
            title: station.title,
            description: station.description,
            image: station.image,
            source_type: 'RADIO',
            station_data: station,
            duration: 0, // Live stream
            is_live: true,
            audio_access: station.audio_access ?? true, // Dynamic access from API
            
            // Dynamic URL fields - pass ALL from API (no hardcoding)
            radio_url: station.radio_url,
            stream_url: station.stream_url,
            mp3_url: station.mp3_url,
            aac_audio_url: station.aac_audio_url,
            mp3_audio_upload: station.mp3_audio_upload,
            aac_audio_upload: station.aac_audio_upload,
            unified_audio_url: station.unified_audio_url,
            embed_url: station.embed_url,
            mp4_url: station.mp4_url,
            hls_url: station.hls_url,
            dash_url: station.dash_url,
            
            // Pass through any other dynamic fields from API
            ...Object.keys(station)
                .filter(key => key.includes('url') || key.includes('stream'))
                .reduce((acc, key) => {
                    acc[key] = station[key];
                    return acc;
                }, {})
        };
        
        console.log('📡 API RESPONSE DEBUG - What URLs are coming from your API:', {
            stationId: station.id,
            stationTitle: station.title,
            
            // CHECK: What audio URLs does your API actually provide?
            actualApiResponse: {
                mp3_url: station.mp3_url || 'NOT PROVIDED BY API',
                aac_audio_url: station.aac_audio_url || 'NOT PROVIDED BY API',
                mp3_audio_upload: station.mp3_audio_upload || 'NOT PROVIDED BY API',
                aac_audio_upload: station.aac_audio_upload || 'NOT PROVIDED BY API',
                embed_url: station.embed_url || 'NOT PROVIDED BY API',
                radio_url: station.radio_url || 'NOT PROVIDED BY API',
                stream_url: station.stream_url || 'NOT PROVIDED BY API',
                unified_audio_url: station.unified_audio_url || 'NOT PROVIDED BY API'
            },
            
            // CHECK: What fields exist in the API response?
            allApiFields: Object.keys(station).filter(key => 
                key.includes('url') || 
                key.includes('stream') || 
                key.includes('audio') || 
                key.includes('mp3') || 
                key.includes('aac')
            ),
            
            // RESULT: What we're sending to the player
            playerPayload: {
                id: audioPayload.id,
                audio_access: audioPayload.audio_access,
                hasAnyUrl: !!(station.mp3_url || station.aac_audio_url || station.mp3_audio_upload || 
                            station.aac_audio_upload || station.radio_url || station.stream_url)
            }
        });
        
        dispatch({
            type: 'GET_CURRENT_SUCCESS',
            payload: audioPayload
        });
        
        // Set radio-specific current station
        dispatch({
            type: RADIO_STATION_ACTIONS.SET_RADIO_CURRENT_STATION,
            payload: station
        });
        
        // Start playback
        dispatch({
            type: 'GET_PLAYING_SUCCESS',
            isPlaying: true
        });
        
        dispatch({
            type: 'GET_CURRENT_PAGE',
            inPage: 'radio'
        });
        
        // Set action type to STATION for radio playback
        dispatch({
            type: 'GET_ACTION_SUCCESS',
            actionType: 'STATION'
        });
        
        // Show floating player
        dispatch({ type: 'OPEN_PLAYLIST', payload: true });
        dispatch({ type: 'MINIMIZE_PLAYER', payload: false });
        
        // Record listening activity
        if (getItem('user_id')) {
            recordListeningActivity({
                source_id: station.id,
                source_type: 2, // Radio stations
                Login_user_type: parseInt(getItem('role'), 10) || 9,
                Login_user_id: parseInt(getItem('user_id'), 10)
            });
        }
        
        toast.success(i18n.t('Playing {{title}}', { title: station.title }));
        
    } catch (error) {
        console.error('Play radio station error:', error);
        toast.error(i18n.t('Failed to play radio station'));
    }
};

/**
 * Pause/Resume Radio Station
 */
export const toggleRadioPlaybackAction = () => (dispatch, getState) => {
    const currentState = getState();
    const isCurrentlyPlaying = currentState.get_current_Reducers.currentPlaying;
    
    dispatch({
        type: 'GET_PLAYING_SUCCESS',
        isPlaying: !isCurrentlyPlaying
    });
};

/**
 * Stop Radio Station
 */
export const stopRadioStationAction = () => (dispatch) => {
    dispatch({ type: 'GET_PLAYING_SUCCESS', isPlaying: false });
    dispatch({ type: 'OPEN_PLAYLIST', payload: false });
    dispatch({ type: RADIO_STATION_ACTIONS.SET_RADIO_CURRENT_STATION, payload: null });
};

/**
 * Set Radio Volume
 */
export const setRadioVolumeAction = (volume) => (dispatch) => {
    dispatch({
        type: RADIO_STATION_ACTIONS.SET_RADIO_VOLUME,
        payload: Math.max(0, Math.min(100, volume))
    });
};

/**
 * Toggle Radio Mute
 */
export const toggleRadioMuteAction = () => (dispatch, getState) => {
    const currentState = getState();
    const isMuted = currentState.get_radio_Reducers?.isMuted || false;
    
    dispatch({
        type: RADIO_STATION_ACTIONS.SET_RADIO_MUTED,
        payload: !isMuted
    });
};

/**
 * Toggle EPG Modal
 */
export const toggleEPGModalAction = (isOpen) => (dispatch) => {
    dispatch({
        type: RADIO_STATION_ACTIONS.TOGGLE_RADIO_EPG_MODAL,
        payload: isOpen
    });
};

/**
 * Toggle Queue Modal
 */
export const toggleQueueModalAction = (isOpen) => (dispatch) => {
    dispatch({
        type: RADIO_STATION_ACTIONS.TOGGLE_RADIO_QUEUE_MODAL,
        payload: isOpen
    });
};

/**
 * Get Radio Station EPG (Electronic Program Guide)
 */
export const getRadioEPGAction = (stationId, date = new Date()) => async (dispatch) => {
    try {
        dispatch({ type: RADIO_STATION_ACTIONS.GET_RADIO_EPG_START });
        
        // Mock EPG data - replace with actual API call when available
        const mockEPG = {
            date: date.toISOString().split('T')[0],
            programs: [
                {
                    id: 1,
                    title: 'Morning Show',
                    description: 'Start your day with music and news',
                    start_time: '06:00',
                    end_time: '09:00',
                    is_current: new Date().getHours() >= 6 && new Date().getHours() < 9
                },
                {
                    id: 2,
                    title: 'Music Mix',
                    description: 'Non-stop music hits',
                    start_time: '09:00',
                    end_time: '12:00',
                    is_current: new Date().getHours() >= 9 && new Date().getHours() < 12
                },
                {
                    id: 3,
                    title: 'Lunch Break',
                    description: 'Relaxing tunes for your break',
                    start_time: '12:00',
                    end_time: '14:00',
                    is_current: new Date().getHours() >= 12 && new Date().getHours() < 14
                },
                {
                    id: 4,
                    title: 'Afternoon Drive',
                    description: 'Energetic music for your drive',
                    start_time: '14:00',
                    end_time: '18:00',
                    is_current: new Date().getHours() >= 14 && new Date().getHours() < 18
                },
                {
                    id: 5,
                    title: 'Evening Special',
                    description: 'Special evening programming',
                    start_time: '18:00',
                    end_time: '22:00',
                    is_current: new Date().getHours() >= 18 && new Date().getHours() < 22
                },
                {
                    id: 6,
                    title: 'Night Music',
                    description: 'Smooth music for the night',
                    start_time: '22:00',
                    end_time: '06:00',
                    is_current: new Date().getHours() >= 22 || new Date().getHours() < 6
                }
            ]
        };
        
        dispatch({
            type: RADIO_STATION_ACTIONS.GET_RADIO_EPG_SUCCESS,
            payload: mockEPG
        });
        
    } catch (error) {
        console.error('EPG fetch error:', error);
        dispatch({
            type: RADIO_STATION_ACTIONS.GET_RADIO_EPG_FAILED,
            payload: error.message || 'Failed to load program guide'
        });
    }
};

/**
 * Clear Radio Stations
 */
export const clearRadioStationsAction = () => (dispatch) => {
    dispatch({ type: RADIO_STATION_ACTIONS.CLEAR_RADIO_STATIONS });
};

/**
 * Clear Radio Station Details
 */
export const clearRadioStationDetailsAction = () => (dispatch) => {
    dispatch({ type: RADIO_STATION_ACTIONS.CLEAR_RADIO_STATION_DETAILS });
};

// Export all actions
const RadioStationActions = {
    getRadioStationsAction,
    getRadioStationDetailsAction,
    playRadioStationAction,
    toggleRadioPlaybackAction,
    stopRadioStationAction,
    setRadioVolumeAction,
    toggleRadioMuteAction,
    toggleEPGModalAction,
    toggleQueueModalAction,
    getRadioEPGAction,
    clearRadioStationsAction,
    clearRadioStationDetailsAction
};
export default RadioStationActions;